import React, { useState } from "react";
import { Modal, Table, Input, Drawer, Popover, Tooltip, Select } from "antd";
import { Button } from "reactstrap";
import { CommentOutlined } from "@ant-design/icons";
import { CheckCircleTwoTone, CloseCircleTwoTone, ClockCircleTwoTone } from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import tenderApis from "../../../api/tender/tenderApis";
import { useSelector } from "react-redux";

// Approval type for ApprovalPopover
type Approval = {
  name: string;
  role: string;
  type: string;
  dateTime: string;
  status?: string;
};

const EvaluationQuestionersSection: React.FC<{
  supplierId?: string;
  questionnaireResponses?: any[];
  tenderId?: number;
  bidId?: number;
  bidType?: string;
  onRefresh?: () => void;
}> = ({
  supplierId,
  questionnaireResponses = [],
  tenderId,
  bidId,
  bidType = "Supplier",
  onRefresh,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState<any>(null);
  const [inputScores, setInputScores] = useState<{
    [section: string]: { [qIdx: number]: any };
  }>({});
  const [inputRemarks, setInputRemarks] = useState<{
    [section: string]: { [qIdx: number]: any };
  }>({});
  const [selectedAnswers, setSelectedAnswers] = useState<{
    [section: string]: { [qIdx: number]: any };
  }>({});
  const [addCommentOpen, setAddCommentOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedEvaluatorId, setSelectedEvaluatorId] = useState<number | null>(null);

  // Get current user from Redux store
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const currentUserId = currentUser?.id || currentUser?.userId;
  // Admin users get access to all sections
  const isAdmin = ["admin", "Admin", "ADMIN"].includes(
    currentUser?.orgRole || ""
  ) || ["admin", "Admin", "ADMIN"].includes(currentUser?.genericRole || "");

  // Build evaluationSections from questionnaireResponses
  const evaluationSections = (questionnaireResponses || []).map(
    (section: any) => {
      // Check if current user is an evaluator for this section
      const evaluators = section.evaluators || [];
      const isEvaluator = isAdmin || evaluators.some(
        (ev: any) =>
          String(ev.userId) === String(currentUserId) ||
          String(ev.evaluatorId) === String(currentUserId) ||
          String(ev.userId) === String(currentUser?.id) ||
          String(ev.evaluatorId) === String(currentUser?.id)
      );
      return {
        sectionName: section.sectionName.toUpperCase() + " SECTION",
        sectionId: section.sectionId,
        questions: section.responses,
        approvals: section.approvals || [],
        weightage: section.sectionWeightage ?? "-",
        evaluators,
        hasAccess: isEvaluator,
      };
    }
  );

  // Fetch and populate scores for a given evaluator userId
  const fetchAndPopulateScores = async (section: any, evaluatorUserId: number) => {
    if (!tenderId || !bidId) return;
    try {
      const scoresRes = await tenderApis.fetchSectionScores({
        tenderId,
        bidId,
        bidType,
        sectionId: section.sectionId,
        evaluatorId: evaluatorUserId,
      });
      if (scoresRes?.status && scoresRes?.data) {
        const sectionName = section.sectionName
          .replace(" SECTION", "")
          .trim()
          .toLowerCase();

        const scores: { [qIdx: number]: any } = {};
        const remarks: { [qIdx: number]: any } = {};

        const allQuestions = scoresRes.data.questions || [];

        // Filter by evaluator userId
        const evaluatorScores = allQuestions.filter(
          (qs: any) => String(qs.evaluatorId) === String(evaluatorUserId)
        );

        // For duplicate questionIds, pick the latest by scoredAt
        const latestByQuestion: { [questionId: number]: any } = {};
        evaluatorScores.forEach((qs: any) => {
          const existing = latestByQuestion[qs.questionId];
          if (!existing || new Date(qs.scoredAt) > new Date(existing.scoredAt)) {
            latestByQuestion[qs.questionId] = qs;
          }
        });

        // Map to inputScores and inputRemarks by question index
        const questions = section.questions || [];
        questions.forEach((q: any, idx: number) => {
          const matchedScore = latestByQuestion[q.questionId];
          if (matchedScore) {
            scores[idx] = matchedScore.score;
            remarks[idx] = matchedScore.remarks || "";
          }
        });

        setInputScores((prev) => ({ ...prev, [sectionName]: scores }));
        setInputRemarks((prev) => ({ ...prev, [sectionName]: remarks }));
      }
    } catch (err) {
      console.error("[EvalSection] Failed to fetch section scores:", err);
    }
  };

  // Open drawer and set selected section only (do not reset input values)
  // If the current evaluator's submissionStatus is "submitted", fetch section scores
  const handleViewSection = async (section: any) => {
    setSelectedSection(section);
    setDrawerOpen(true);

    const evaluators = section.evaluators || [];

    if (isAdmin) {
      // Admin: reset selected evaluator, wait for dropdown selection
      setSelectedEvaluatorId(null);
      // Clear previous scores for this section
      const sectionName = section.sectionName
        .replace(" SECTION", "")
        .trim()
        .toLowerCase();
      setInputScores((prev) => ({ ...prev, [sectionName]: {} }));
      setInputRemarks((prev) => ({ ...prev, [sectionName]: {} }));
    } else {
      // Regular evaluator: check own submissionStatus
      const currentEvaluator = evaluators.find(
        (ev: any) =>
          String(ev.userId) === String(currentUserId) ||
          String(ev.userId) === String(currentUser?.id) ||
          String(ev.tenderSectionEvaluatorId) === String(currentUserId) ||
          String(ev.tenderSectionEvaluatorId) === String(currentUser?.id)
      );

      if (currentEvaluator?.submissionStatus === "submitted") {
        await fetchAndPopulateScores(section, currentEvaluator.userId);
      }
    }
  };

  // Admin selects an evaluator from dropdown → fetch their scores if submitted
  const handleEvaluatorChange = async (evaluatorUserId: number) => {
    setSelectedEvaluatorId(evaluatorUserId);
    if (!selectedSection) return;

    // Clear previous scores
    const sectionName = selectedSection.sectionName
      .replace(" SECTION", "")
      .trim()
      .toLowerCase();
    setInputScores((prev) => ({ ...prev, [sectionName]: {} }));
    setInputRemarks((prev) => ({ ...prev, [sectionName]: {} }));

    // Check if this evaluator has submitted
    const evaluators = selectedSection.evaluators || [];
    const evaluator = evaluators.find(
      (ev: any) => String(ev.userId) === String(evaluatorUserId)
    );
    if (evaluator?.submissionStatus === "submitted") {
      await fetchAndPopulateScores(selectedSection, evaluatorUserId);
    }
  };

  const currentSectionName = selectedSection?.sectionName
    ?.replace(" SECTION", "")
    .toLowerCase()
    .trim();

  // Access check for the currently selected/opened section
  const hasAccess = selectedSection?.hasAccess ?? false;

  const handleScoreChange = (sectionKey: string, idx: number, value: any) => {
    setInputScores((prev) => ({
      ...prev,
      [sectionKey]: { ...(prev[sectionKey] || {}), [idx]: value },
    }));
  };
  const handleAnswerChange = (
    sectionKey: string,
    idx: number,
    value: any,
    type: string
  ) => {
    setSelectedAnswers((prev) => {
      const current = prev[sectionKey]?.[idx];
      if (type === "Multiple Choice") {
        const arr = Array.isArray(current) ? [...current] : [];
        if (arr.includes(value)) {
          return {
            ...prev,
            [sectionKey]: {
              ...(prev[sectionKey] || {}),
              [idx]: arr.filter((v) => v !== value),
            },
          };
        } else {
          return {
            ...prev,
            [sectionKey]: {
              ...(prev[sectionKey] || {}),
              [idx]: [...arr, value],
            },
          };
        }
      }
      return {
        ...prev,
        [sectionKey]: { ...(prev[sectionKey] || {}), [idx]: value },
      };
    });
  };

  const handleRemarksChange = (sectionKey: string, idx: number, value: any) => {
    setInputRemarks((prev) => ({
      ...prev,
      [sectionKey]: { ...(prev[sectionKey] || {}), [idx]: value },
    }));
  };

  const getButtonStyle = (enabled: boolean): React.CSSProperties => ({
    background: enabled ? "#2263b3" : "#b3c6e6",
    color: enabled ? "#fff" : "#f5f5f5",
    fontWeight: 500,
    fontFamily: "inherit",
    fontSize: 13,
    borderRadius: 4,
    border: "none",
    boxShadow: "none",
    minHeight: 40,
    minWidth: 100,
    cursor: enabled ? "pointer" : "not-allowed",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 16px",
  });

  function capitalizeWords(str: string) {
    return str.replace(/\b\w/g, (c: string) => c.toUpperCase());
  }

  // Main table columns
  const sectionTableColumns = [
    {
      title: "Section",
      dataIndex: "sectionName",
      key: "sectionName",
      width: 320,
      render: (text: string) => capitalizeWords(text.toLowerCase()),
    },
    {
      title: "Sectionwise Weightage",
      dataIndex: "weightage",
      key: "weightage",
      width: 160,
      render: (val: any) => (val != null && val !== "-" ? `${val}%` : "-"),
    },
    {
      title: "Evaluators",
      key: "approvals",
      width: 160,
      render: (_: any, record: any) => {
        const sectionApprovals: Approval[] = record.approvals || [];
        return sectionApprovals.length > 0 ? (
          <Popover
            content={
              <ApprovalPopover approvals={sectionApprovals} hasAccess={hasAccess} />
            }
            placement="right"
            trigger={["hover"]}
            overlayStyle={{ minWidth: 220 }}
          >
            <span
              style={{
                color: "#2263b3",
                textDecoration: "underline",
                cursor: "pointer",
                fontWeight: 500,
                fontSize: 15,
                margin: 0,
                padding: 0,
                lineHeight: 1.2,
              }}
            >
              {sectionApprovals.length} Evaluator(s)
            </span>
          </Popover>
        ) : (
          <span style={{ color: "#888" }}>--</span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_: any, record: any) => {
        const evList = record.evaluators || [];
        const myEvaluator = evList.find(
          (ev: any) =>
            String(ev.userId) === String(currentUserId) ||
            String(ev.userId) === String(currentUser?.id)
        );
        const isSubmitted = myEvaluator?.submissionStatus === "submitted";
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "#2263b3",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: 15,
              margin: 0,
              padding: 0,
              lineHeight: 1.2,
            }}
            onClick={() => handleViewSection(record)}
          >
            {record.hasAccess ? "Evaluate" : "View"}
            {isSubmitted && (
              <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 16 }} />
            )}
          </span>
        );
      },
    },
  ];

  return (
    <div>
      <Table
        columns={sectionTableColumns}
        dataSource={evaluationSections.map((s, i) => ({ ...s, key: i }))}
        pagination={false}
        bordered
        style={{ background: "white" }}
        scroll={{ x: true }}
        size="small"
      />
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={1200}
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 18 }}>
              {selectedSection?.sectionName}
            </span>
            {!!selectedSection && hasAccess && (
              <Button
                color="primary"
                style={getButtonStyle(true)}
                onClick={() => setAddCommentOpen(true)}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <CommentOutlined style={{ fontSize: 18 }} />
                  <span>Add Comments</span>
                </span>
              </Button>
            )}
          </div>
        }
        styles={{ body: { padding: 0, overflow: "auto" } }}
      >
        {/* Admin: Evaluator dropdown */}
        {selectedSection && isAdmin && (
          <div
            style={{
              padding: "14px 24px",
              background: "#f5f7fa",
              borderBottom: "1px solid #e3eafc",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span
              style={{
                fontWeight: 600,
                fontSize: 14,
                color: "#333",
                whiteSpace: "nowrap",
              }}
            >
              Evaluator:
            </span>
            <Select
              style={{ minWidth: 250 }}
              placeholder="-- Select Evaluator --"
              value={selectedEvaluatorId ?? undefined}
              onChange={(val: number) => handleEvaluatorChange(val)}
              options={(selectedSection?.evaluators || []).map((ev: any) => ({
                label: ev.submissionStatus === "submitted"
                  ? `${ev.userName} (Submitted)`
                  : ev.userName,
                value: ev.userId,
              }))}
            />
          </div>
        )}
        {selectedSection &&
          (() => {
            const selectedSectionName = (selectedSection?.sectionName || "")
              .replace(" SECTION", "")
              .trim()
              .toLowerCase();

            const supplierSectionResponse = questionnaireResponses.find(
              (s) => s.sectionName.trim().toLowerCase() === selectedSectionName
            );
            const questionsWithAnswers =
              supplierSectionResponse?.responses?.map((q: any) => {
                return { ...q };
              }) || [];

            // Check if the active evaluator has already submitted
            const evaluators = selectedSection?.evaluators || [];
            const isEvaluatorSubmitted = (() => {
              if (isAdmin) {
                if (!selectedEvaluatorId) return false;
                const ev = evaluators.find((e: any) => Number(e.userId) === Number(selectedEvaluatorId));
                return ev?.submissionStatus === "submitted";
              }
              const myEv = evaluators.find(
                (e: any) =>
                  String(e.userId) === String(currentUserId) ||
                  String(e.userId) === String(currentUser?.id)
              );
              return myEv?.submissionStatus === "submitted";
            })();

            // Calculate total question weightage for normalization
            const totalQWeight = questionsWithAnswers.reduce(
              (sum: number, qq: any) => sum + (qq.weightage || 0),
              0
            );

            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {questionsWithAnswers.map((q: any, idx: number) => (
                  <div
                    key={idx}
                    style={{
                      background: "#fcfcfd",
                      borderRadius: 10,
                      boxShadow: "0 1px 4px rgba(34,99,179,0.04)",
                      padding: "20px 24px",
                      marginBottom: 18,
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      gap: 32,
                      border: "1px solid #e3eafc",
                      fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
                    }}
                  >
                    {/* Left: Question and Answer */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 500,
                          color: "#111",
                          fontSize: 17,
                          marginBottom: 10,
                          fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
                        }}
                      >
                        {q.question}
                      </div>
                      {/* Answer rendering */}
                      {(() => {
                        const sectionName = selectedSection?.sectionName
                          ?.replace(" SECTION", "")
                          .trim()
                          .toLowerCase();
                        // Documents section: show Download link for File Upload
                        if (
                          sectionName === "documents" &&
                          q.type === "File Upload"
                        ) {
                          let docName = q.question
                            .replace(/^Upload /i, "")
                            .replace(/your /i, "")
                            .replace(/\(if applicable\)/i, "")
                            .replace(/\.$/, "")
                            .replace(/\s+/g, " ")
                            .trim()
                            .replace(/\b\w/g, (c: string) => c.toUpperCase());
                          return (
                            <span>
                              View Docs:{" "}
                              <a
                                href={q.answer}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#2263b3",
                                  textDecoration: "underline",
                                  fontWeight: 500,
                                }}
                              >
                                {docName}
                              </a>
                            </span>
                          );
                        }
                        if (
                          [
                            "Yes/No",
                            "Single Choice",
                            "Multiple Choice",
                          ].includes(q.type)
                        ) {
                          // Use selectedAnswers state if user changed, otherwise fall back to supplier's answer
                          const stateVal = selectedAnswers[currentSectionName]?.[idx];
                          const defaultVal = Array.isArray(q.answer)
                            ? q.answer
                            : [q.answer];
                          const selected =
                            stateVal !== undefined
                              ? Array.isArray(stateVal)
                                ? stateVal
                                : [stateVal]
                              : defaultVal;
                          return (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                gap: 10,
                                marginTop: 2,
                                flexWrap: "wrap",
                              }}
                            >
                              {(q.options || []).map((opt: string, i: number) => {
                                return (
                                  <label
                                    key={i}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      background: selected.includes(opt)
                                        ? "#eafaf1"
                                        : "#f5f7fa",
                                      borderRadius: 6,
                                      padding: "2px 10px",
                                      border: selected.includes(opt)
                                        ? "1.5px solid #27ae60"
                                        : "1px solid #e0e0e0",
                                      fontWeight: selected.includes(opt)
                                        ? 600
                                        : 500,
                                      color: selected.includes(opt)
                                        ? "#111"
                                        : "#222",
                                      fontSize: 15,
                                      minWidth: 70,
                                      minHeight: 24,
                                      height: 24,
                                      justifyContent: "center",
                                      cursor: hasAccess ? "pointer" : "default",
                                      opacity: hasAccess ? 1 : 0.7,
                                    }}
                                  >
                                    <input
                                      type={
                                        q.type === "Multiple Choice"
                                          ? "checkbox"
                                          : "radio"
                                      }
                                      checked={selected.includes(opt)}
                                      disabled={!hasAccess}
                                      onChange={() =>
                                        handleAnswerChange(
                                          currentSectionName,
                                          idx,
                                          opt,
                                          q.type
                                        )
                                      }
                                      style={{
                                        accentColor: selected.includes(opt)
                                          ? "#27ae60"
                                          : "#b3c6e6",
                                        marginRight: 3,
                                        width: 12,
                                        height: 12,
                                        cursor: hasAccess ? "pointer" : "default",
                                      }}
                                    />
                                    <span>{opt}</span>
                                  </label>
                                );
                              })}
                            </div>
                          );
                        }
                        if (["Short Text", "Long Text"].includes(q.type)) {
                          return (
                            <div
                              style={{
                                marginTop: 8,
                                color: "#222",
                                fontWeight: 400,
                                fontSize: 15,
                                fontFamily:
                                  "'Inter', 'Segoe UI', Arial, sans-serif",
                              }}
                            >
                              {(q.answer || "")
                                .split(/\r?\n/)
                                .map((line: string, i: number) =>
                                  line.trim() === "" ? (
                                    <div key={i} style={{ height: "8px" }} />
                                  ) : (
                                    <span key={i}>
                                      {line}
                                      <br />
                                    </span>
                                  )
                                )}
                            </div>
                          );
                        }
                        return null;
                      })()}
                      {/* Meta info row */}
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 24,
                          marginTop: 12,
                          fontSize: 13,
                          color: "#888",
                          alignItems: "center",
                          fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
                        }}
                      >
                        <span>
                          <b>Supplier Remarks:</b>{" "}
                          <Tooltip
                            title={
                              <div
                                style={{
                                  color: "#222",
                                  whiteSpace: "pre-line",
                                }}
                              >
                                {q.supplierRemarks || "No remarks"}
                              </div>
                            }
                            overlayInnerStyle={{
                              maxWidth: 320,
                              padding: 16,
                              borderRadius: 8,
                              boxShadow: "0 2px 12px rgba(34,99,179,0.10)",
                              background: "#fff",
                              color: "#222",
                              fontSize: 14,
                              lineHeight: 1.6,
                            }}
                          >
                            <span
                              style={{
                                color: "#2263b3",
                                textDecoration: "underline",
                                cursor: "pointer",
                                fontWeight: 500,
                              }}
                            >
                              View
                            </span>
                          </Tooltip>
                        </span>
                        <span>
                          <b>Weightage:</b>{" "}
                          {(() => {
                            const sectionWeight = supplierSectionResponse?.sectionWeightage || 0;
                            if (totalQWeight > 0 && sectionWeight > 0) {
                              return `${(((q.weightage || 0) / totalQWeight) * sectionWeight).toFixed(2)}%`;
                            }
                            return "-";
                          })()}
                        </span>
                        <span>
                          <Tooltip
                            title={
                              <div
                                style={{
                                  color: "#222",
                                  whiteSpace: "pre-line",
                                }}
                              >
                                {q.comment || "None"}
                              </div>
                            }
                            overlayInnerStyle={{
                              maxWidth: 320,
                              padding: 16,
                              borderRadius: 8,
                              boxShadow: "0 2px 12px rgba(34,99,179,0.10)",
                              background: "#fff",
                              color: "#222",
                              fontSize: 14,
                              lineHeight: 1.6,
                            }}
                          >
                            <span
                              style={{
                                color: "#2263b3",
                                textDecoration: "underline",
                                cursor: "pointer",
                                fontWeight: 500,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <CommentOutlined style={{ fontSize: 15 }} />
                              Comment
                            </span>
                          </Tooltip>
                        </span>
                      </div>
                    </div>
                    {/* Right: Input fields (styled) */}
                    {hasAccess && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          minWidth: 270,
                          maxWidth: 270,
                          marginTop: 0,
                        }}
                      >
                        <div style={{ width: "100%" }}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              gap: 12,
                              width: "100%",
                              marginBottom: 2,
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                fontSize: 16,
                                fontWeight: 600,
                                color: "#111",
                                textAlign: "left",
                                fontFamily:
                                  "'Inter', 'Segoe UI', Arial, sans-serif",
                                whiteSpace: "nowrap",
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              Input Score <span style={{ color: "red", fontSize: 14, lineHeight: 1 }}>*</span>
                            </div>
                            <div
                              style={{
                                flex: 2,
                                fontSize: 16,
                                fontWeight: 600,
                                color: "#111",
                                textAlign: "left",
                                fontFamily:
                                  "'Inter', 'Segoe UI', Arial, sans-serif",
                              }}
                            >
                              Input Remarks
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              gap: 12,
                              width: "100%",
                              alignItems: "center",
                            }}
                          >
                            <input
                              type="number"
                              min={0}
                              max={20}
                              disabled={isEvaluatorSubmitted}
                              value={
                                inputScores[currentSectionName]?.[idx] ?? ""
                              }
                              onChange={(e) =>
                                handleScoreChange(
                                  currentSectionName,
                                  idx,
                                  e.target.value
                                )
                              }
                              style={{
                                flex: 1,
                                width: "100%",
                                padding: "4px 6px",
                                borderRadius: 4,
                                border: "1px solid #b3c6e6",
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#1a237e",
                                fontFamily:
                                  "'Inter', 'Segoe UI', Arial, sans-serif",
                                boxShadow: "0 1px 2px rgba(34,99,179,0.06)",
                                outline: "none",
                                transition: "border 0.2s, box-shadow 0.2s",
                                height: 24,
                                display: "flex",
                                alignItems: "center",
                                background: isEvaluatorSubmitted ? "#f0f0f0" : "#fff",
                              }}
                              onFocus={(e) =>
                                (e.target.style.border = "1.5px solid #bdbdbd")
                              }
                              onBlur={(e) =>
                                (e.target.style.border = "1px solid #b3c6e6")
                              }
                            />
                            <input
                              type="text"
                              disabled={isEvaluatorSubmitted}
                              value={
                                inputRemarks[currentSectionName]?.[idx] ?? ""
                              }
                              onChange={(e) =>
                                handleRemarksChange(
                                  currentSectionName,
                                  idx,
                                  e.target.value
                                )
                              }
                              style={{
                                flex: 2,
                                width: "100%",
                                padding: "4px 6px",
                                borderRadius: 4,
                                border: "1px solid #b3c6e6",
                                fontSize: 14,
                                fontWeight: 600,
                                color: "#1a237e",
                                fontFamily:
                                  "'Inter', 'Segoe UI', Arial, sans-serif",
                                boxShadow: "0 1px 2px rgba(34,99,179,0.06)",
                                outline: "none",
                                transition: "border 0.2s, box-shadow 0.2s",
                                height: 24,
                                display: "flex",
                                alignItems: "center",
                                background: isEvaluatorSubmitted ? "#f0f0f0" : "#fff",
                              }}
                              onFocus={(e) =>
                                (e.target.style.border = "1.5px solid #bdbdbd")
                              }
                              onBlur={(e) =>
                                (e.target.style.border = "1px solid #b3c6e6")
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {/* Submit Button */}
                {hasAccess && !isEvaluatorSubmitted && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      padding: "20px 24px",
                      marginTop: 10,
                    }}
                  >
                    <Button
                      color="primary"
                      style={getButtonStyle(!submitting)}
                      disabled={submitting}
                      onClick={async () => {
                        if (!tenderId || !bidId) {
                          Swal.fire("Error", "Missing tender or bid information.", "error");
                          return;
                        }

                        // Mandatory validation: all questions must have scores
                        const missingScores: number[] = [];
                        questionsWithAnswers.forEach((_: any, qIdx: number) => {
                          const score = inputScores[currentSectionName]?.[qIdx];
                          if (score === undefined || score === null || score === "") {
                            missingScores.push(qIdx + 1);
                          }
                        });
                        if (missingScores.length > 0) {
                          Swal.fire(
                            "Validation Error",
                            `Please provide scores for all questions. Missing: Question ${missingScores.join(", ")}`,
                            "warning"
                          );
                          return;
                        }

                        const sectionData = supplierSectionResponse;
                        const sectionId = sectionData?.sectionId;
                        // For admin: use selected evaluator's userId; for regular evaluator: use own userId
                        let evaluatorId: any;
                        if (isAdmin) {
                          if (!selectedEvaluatorId) {
                            Swal.fire("Validation Error", "Please select an evaluator before submitting.", "warning");
                            return;
                          }
                          evaluatorId = selectedEvaluatorId;
                        } else {
                          const sectionEvaluators = sectionData?.evaluators || [];
                          const currentEvaluator = sectionEvaluators.find(
                            (ev: any) => String(ev.userId) === String(currentUserId) || String(ev.evaluatorId) === String(currentUserId)
                          );
                          evaluatorId = currentEvaluator?.userId || currentEvaluator?.evaluatorId || sectionData?.evaluatorId;
                        }

                        const questionScores = questionsWithAnswers.map(
                          (q: any, qIdx: number) => {
                            // Get selected answer - from state if changed, otherwise from supplier's original answer
                            const stateVal = selectedAnswers[currentSectionName]?.[qIdx];
                            let answer: any;
                            if (stateVal !== undefined) {
                              answer = Array.isArray(stateVal) ? stateVal : [stateVal];
                            } else if (Array.isArray(q.answer)) {
                              answer = q.answer;
                            } else {
                              answer = q.answer && q.answer !== "--" ? [q.answer] : [];
                            }

                            return {
                              questionId: q.questionId,
                              score: Number(inputScores[currentSectionName]?.[qIdx]) || 0,
                              remarks: inputRemarks[currentSectionName]?.[qIdx] || "",
                              comments: "",
                              selectedOptions: answer,
                            };
                          }
                        );

                        const payload = {
                          tenderId,
                          bidId,
                          bidType,
                          sectionId,
                          evaluatorId,
                          questionScores,
                          sectionRemarks: comment || "",
                          evaluatorComments: "",
                        };

                        setSubmitting(true);
                        try {
                          const res = await tenderApis.submitSectionEvaluation(payload);
                          if (res?.status) {
                            // Fetch section scores after successful submit
                            try {
                              const scoresRes = await tenderApis.fetchSectionScores({
                                tenderId,
                                bidId,
                                bidType,
                                sectionId,
                                evaluatorId,
                              });
                              console.log("[EvalSection] Section scores:", scoresRes);
                            } catch (err) {
                              console.error("[EvalSection] Failed to fetch section scores:", err);
                            }
                            Swal.fire("Success", "Section evaluation submitted successfully.", "success");
                            setDrawerOpen(false);
                            onRefresh?.();
                          } else {
                            Swal.fire("Error", res?.message || "Failed to submit evaluation.", "error");
                          }
                        } catch (error: any) {
                          Swal.fire("Error", error?.message || "Something went wrong.", "error");
                        } finally {
                          setSubmitting(false);
                        }
                      }}
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
      </Drawer>
      <Modal
        open={addCommentOpen}
        onCancel={() => setAddCommentOpen(false)}
        title="Add Comments"
        footer={null}
        getContainer={() => document.body}
        zIndex={2000}
      >
        <Input.TextArea
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Enter your comments here..."
        />
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}
        >
          <Button
            color="primary"
            style={getButtonStyle(true)}
            onClick={() => setAddCommentOpen(false)}
          >
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
};

// ApprovalPopover component
export const ApprovalPopover: React.FC<{
  approvals: Approval[];
  hasAccess: boolean;
}> = ({ approvals, hasAccess }) => (
  <div>
    <div
      style={{
        color: "#2263b3",
        fontWeight: 600,
        fontSize: 18,
        marginBottom: 8,
      }}
    >
      Approval Hierarchy
    </div>
    <div style={{ position: "relative", paddingLeft: 28 }}>
      {approvals.length === 0 ? (
        <div style={{ color: "#888", fontSize: 14 }}>
          No approval data available.
        </div>
      ) : (
        approvals.map((a, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "flex-start",
              marginBottom: i !== approvals.length - 1 ? 18 : 0,
              position: "relative",
              minHeight: 64,
            }}
          >
            <div
              style={{
                position: "relative",
                marginRight: 8,
                width: 20,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                height: "100%",
              }}
            >
              <span style={{ zIndex: 1 }}>
                {a.status === "APPROVED" ? (
                  <CheckCircleTwoTone
                    twoToneColor="#52c41a"
                    style={{ fontSize: 20, marginTop: 2 }}
                  />
                ) : a.status === "REJECTED" ? (
                  <CloseCircleTwoTone
                    twoToneColor="#ff4d4f"
                    style={{ fontSize: 20, marginTop: 2 }}
                  />
                ) : (
                  <ClockCircleTwoTone
                    twoToneColor="#faad14"
                    style={{ fontSize: 20, marginTop: 2 }}
                  />
                )}
              </span>
              {i !== approvals.length - 1 && (
                <div
                  style={{
                    position: "absolute",
                    top: 30,
                    left: "50%",
                    width: 2,
                    height: 70,
                    background: "#e0e0e0",
                    transform: "translateX(-50%)",
                    zIndex: 0,
                  }}
                />
              )}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 0,
                minWidth: 120,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 15, color: "#222" }}>
                {a.name}
              </span>
              <span style={{ fontSize: 13, color: "#666", marginTop: 0 }}>
                {a.role}
              </span>
              <span style={{ fontSize: 13, color: "#666", marginTop: 0 }}>
                {a.type}
              </span>
              {a.dateTime && (
                <span style={{ fontSize: 13, color: "#666", marginTop: 0 }}>
                  {dayjs(a.dateTime, ["DD-MM-YYYY", "YYYY-MM-DD"]).isValid()
                    ? dayjs(a.dateTime, ["DD-MM-YYYY", "YYYY-MM-DD"]).format(
                        "DD-MMM-YYYY"
                      )
                    : a.dateTime}
                </span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

export default EvaluationQuestionersSection;
